// --- Utilities
import { clearAuthToken, setCurrentUser } from '../../utils/localStorageService';
// --- Authentication Constants
import {
    SHOW_SPINNER,
    SET_AUTH_USER,
    SET_AUTH_TOKEN,
    SET_AUTH_ROLE,
    SET_AUTH_ERROR,
    USER_LOGOUT,
    SET_SESSION_START,
} from './constants';
// --- Third-Party Libraries
import jwtDecode from 'jwt-decode';

/**
 * Action to set the authentication user.
 *
 * This action is dispatched to set the authenticated user in the Redux store.
 * It takes a payload containing the user data.
 *
 * @param token The user token to be set.
 * @returns {Object} The action object with type and payload.
 **/
export const fetchUserPermissions = (token) => async (dispatch) => {
    try {
        dispatch({ type: SHOW_SPINNER, showSpinner: true });
        dispatch({ type: SET_AUTH_ERROR, error: '' });

        // Check if the token is not empty
        if (token !== '') {
            // Decode the token to get user informatioz
            const decodedToken = jwtDecode(token);

            // Set local storage items
            localStorage.setItem('permissions', decodedToken.permissions);
            localStorage.setItem('email', decodedToken.email);
            localStorage.setItem('firstname', decodedToken.firstname);
            localStorage.setItem('lastname', decodedToken.lastname);
            localStorage.setItem('token', token);

            // Set current user
            setCurrentUser(decodedToken.email);

            // Set user auth token
            setAuthToken(token);
            dispatch({ type: SET_AUTH_TOKEN, token });
            dispatch({ type: SET_AUTH_ERROR, error: '' });
            dispatch({ type: SHOW_SPINNER, showSpinner: false });
        }
    } catch (err) {
        dispatch({ type: SET_AUTH_ERROR, error: err.message });
        dispatch({ type: SHOW_SPINNER, showSpinner: false });
    }
};

/**
 * Action to log out the user.
 *
 * This action is dispatched to log out the user from the application.
 *
 * It clears the authentication token, user information, and roles from the Redux store.
 * It also clears the local storage items related to authentication.
 *
 * @returns {function(*): void}
 */

import { apiClientV1 } from '../../api';
import { getAuthToken } from '../../utils/localStorageService';

export const logout = () => async (dispatch) => {
    try {
        // Attempt backend logout if token exists
        const token = getAuthToken();
        if (token) {
            await apiClientV1(token).post('/users/logout');
        }
    } catch (error) {
        // Log but continue with local logout
        console.error('Backend logout failed:', error);
    }
    clearAuthToken();
    dispatch({ type: SET_AUTH_USER, user: '' });
    dispatch({ type: SET_AUTH_TOKEN, token: '' });
    dispatch({ type: SET_AUTH_ROLE, roles: '' });
    dispatch({ type: SET_AUTH_ERROR, error: '' });
    localStorage.setItem('permissions', '');
    localStorage.setItem('email', '');
    localStorage.setItem('firstname', '');
    localStorage.setItem('lastname', '');
    localStorage.setItem('token', '');
    localStorage.setItem('activePanel', '');
    dispatch({ type: USER_LOGOUT });
};

/**
 * Action to set the authentication token.
 *
 * This action is dispatched to set the authentication token in the Redux store.
 * It takes a token as a parameter.
 *
 * @param token
 * @returns {function(*): void}
 */
export const setAuthToken = (token) => (dispatch) => {
    dispatch({ type: SET_AUTH_TOKEN, token });
};

/**
 * Action to set the authentication role.
 *
 * This action is dispatched to set the authentication role in the Redux store.
 * It takes a role as a parameter.
 *
 * @param user
 * @returns {function(*): void}
 */
export const setAuthUser = (user) => (dispatch) => {
    dispatch({ type: SET_AUTH_USER, user });
};

/**
 * Action to set the authentication role.
 *
 * This action is dispatched to set the authentication role in the Redux store.
 * It takes a role as a parameter.
 *
 * @param dateTime
 * @returns {function(*): void}
 */
export const setSessionStart = (dateTime) => (dispatch) => {
    dispatch({ type: SET_SESSION_START, sessionStart: dateTime });
};

export const setAuthError = (error) => (dispatch) => {
    dispatch({ type: SET_AUTH_ERROR, error: error || '' });
};
